import React from 'react';

import {TFunction} from 'i18next';
import {generatePath} from 'react-router-dom';

import {Link} from 'components/ui-kit/Link';
import {ProductsPackageType, ProductStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {breakpoint} from 'theme';
import {ProductContentAccessors, ProductContentFormattedRow, ProductContentRawRow} from 'types';
import {useMatchMedia} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {toHex} from 'utils/string';

import {ProductsList} from './List';
import {ProductsTable} from './Table';

const getConfig = (t: TFunction) => [
    {
        Header: t('pages.products.grid.product'),
        accessor: ProductContentAccessors.name,
        disableSortBy: true,
        width: 200,
    },
    {
        Header: t('pages.products.grid.code'),
        accessor: ProductContentAccessors.code,
        disableSortBy: true,
        width: 260,
    },
    {
        Header: t('pages.products.grid.status'),
        accessor: ProductContentAccessors.status,
        disableSortBy: true,
        width: 150,
    },
    {
        Header: t('pages.products.grid.changeDate'),
        accessor: ProductContentAccessors.modifiedDate,
        disableSortBy: true,
        width: 200,
    },
    {
        Header: t('pages.products.grid.package'),
        accessor: ProductContentAccessors.packageType,
        disableSortBy: true,
        width: 170,
    },
    {
        Header: t('pages.products.grid.manufacturer'),
        accessor: ProductContentAccessors.manufacturer,
        disableSortBy: true,
        width: 200,
    },
];

export function convertData(
    data: ProductContentRawRow[] | undefined,
    t: TFunction,
): ProductContentFormattedRow[] {
    if (!data || !data.length) {
        return [];
    }

    return data.map((elem) => ({
        [ProductContentAccessors.name]: handleNil(elem.productName),
        [ProductContentAccessors.code]: handleNil(elem.markingCode, (value: string) => (
            <Link
                to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                    id: toHex(`${elem.gtin}§${value}`),
                })}
                style={{wordBreak: 'break-all'}}
            >
                {value}
            </Link>
        )),
        [ProductContentAccessors.status]: handleNil(elem.status, (value: ProductStatus) =>
            t(`dictionaries.products.status.${value}`),
        ),
        [ProductContentAccessors.modifiedDate]: handleNil(elem.modifiedAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy'),
        ),
        [ProductContentAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
        [ProductContentAccessors.manufacturer]: handleNil(elem.manufacturerName),
    }));
}

interface Props {
    receiptId: string;
}

export const Products = ({receiptId}: Props) => {
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);

    return (
        <>
            {!isMobile && (
                <ProductsTable
                    receiptId={receiptId}
                    configFunc={getConfig}
                    dataMapper={convertData}
                />
            )}
            {isMobile && (
                <ProductsList
                    receiptId={receiptId}
                    configFunc={getConfig}
                    dataMapper={convertData}
                />
            )}
        </>
    );
};
